import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Clock, Shield, Sparkles } from "lucide-react";
import Button from "../../components/ui/Button";
import { useAppContext } from "../../context/AppContext";

export default function Hero() {
  const { setShowLoginModal } = useAppContext();

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-20">
      {/* Background Ambience */}
      <div className="absolute inset-0 bg-background pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-primary-500/10 blur-[100px] animate-pulse-slow" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-secondary-500/10 blur-[100px] animate-pulse-slow delay-1000" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center lg:text-left"
        >
          <motion.div 
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ delay: 0.2 }}
             className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-300 text-sm font-medium mb-6 backdrop-blur-sm"
          >
            <Sparkles size={14} />
            <span>The Future of Time Management</span>
          </motion.div>

          <h1 className="text-4xl md:text-6xl font-black tracking-tighter leading-[1.1] mb-6 text-white pb-2">
            Stop Juggling, <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400">
              Start Living.
            </span>
          </h1>
          
          <p className="text-base text-gray-400 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed font-light">
            One app for your organizations, appointments, and personal events.
            Experience zero friction and master your timeline with <span className="text-white font-semibold">TimeWise</span>.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
            <Button 
              size="lg" 
              className="w-full sm:w-auto min-w-[160px] shadow-primary-500/25 shadow-lg"
              onClick={() => setShowLoginModal(true)}
              icon={<ArrowRight size={18} />}
            >
              Get Started
            </Button>
            <Button 
              size="lg" 
              variant="secondary"
              className="w-full sm:w-auto min-w-[160px]"
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Learn More
            </Button>
          </div>
        </motion.div>

        {/* Right Visual (Abstract Floating Elements) */}
        <div className="relative hidden lg:flex items-center justify-center h-[600px] perspective-1000">
          {/* Main Glass Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotateX: 20 }}
            animate={{ opacity: 1, scale: 1, rotateX: 0 }}
            transition={{ duration: 1, type: "spring", bounce: 0.4 }}
            className="relative z-20 w-96 bg-surface/40 backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-2xl flex flex-col gap-6"
            style={{
              transformStyle: "preserve-3d",
              rotateY: -15,
            }}
          >
            {/* Fake UI Header */}
            <div className="flex justify-between items-center border-b border-white/5 pb-4">
              <div className="flex gap-2">
                <div className="h-3 w-3 rounded-full bg-red-500/50" />
                <div className="h-3 w-3 rounded-full bg-yellow-500/50" />
                <div className="h-3 w-3 rounded-full bg-green-500/50" />
              </div>
              <div className="h-2 w-20 rounded-full bg-white/10" />
            </div>
            
            <div className="space-y-4">
               {[1, 2, 3].map((i) => (
                 <motion.div 
                    key={i} 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="flex gap-4 items-center p-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors"
                  >
                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
                      i === 1 ? 'bg-primary-500/20 text-primary-400' : 
                      i === 2 ? 'bg-secondary-500/20 text-secondary-400' : 
                      'bg-purple-500/20 text-purple-400'
                    }`}>
                      {i === 1 ? <Clock size={20} /> : i === 2 ? <Calendar size={20} /> : <Shield size={20} />}
                    </div>
                    <div className="space-y-2 flex-1">
                      <div className="h-2 w-24 rounded-full bg-white/20" />
                      <div className="h-1.5 w-16 rounded-full bg-white/10" />
                    </div>
                 </motion.div>
               ))}
            </div>

            <div className="mt-auto">
              <div className="h-32 rounded-xl bg-gradient-to-br from-primary-500/20 to-secondary-500/20 border border-white/5 relative overflow-hidden group">
                 <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                 <div className="absolute bottom-4 left-4 right-4">
                    <div className="h-2 w-1/2 bg-white/30 rounded-full mb-2" />
                    <div className="flex gap-1">
                       <div className="h-8 w-1 flex-1 bg-primary-500/40 rounded-sm" />
                       <div className="h-6 w-1 flex-1 bg-primary-500/30 rounded-sm" />
                       <div className="h-10 w-1 flex-1 bg-primary-500/50 rounded-sm" />
                       <div className="h-5 w-1 flex-1 bg-primary-500/20 rounded-sm" />
                       <div className="h-7 w-1 flex-1 bg-primary-500/40 rounded-sm" />
                    </div>
                 </div>
              </div>
            </div>
          </motion.div>

          {/* Floating Elements (Background) */}
          <motion.div
            animate={{ y: [-20, 20, -20] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 right-0 z-10 p-4 bg-surface/80 backdrop-blur-md rounded-2xl border border-white/10 shadow-xl"
          >
            <Calendar className="text-secondary-400" size={32} />
          </motion.div>
          
          <motion.div
            animate={{ y: [15, -15, 15] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute bottom-40 left-10 z-30 p-4 bg-surface/80 backdrop-blur-md rounded-2xl border border-white/10 shadow-xl"
            style={{ rotate: -10 }}
          >
            <Shield className="text-primary-400" size={32} />
          </motion.div>
        </div>
      </div>
      
      {/* Scroll indicator */}
       <motion.div 
         animate={{ y: [0, 10, 0] }}
         transition={{ duration: 1.5, repeat: Infinity }}
         className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-500 cursor-pointer hover:text-white transition-colors"
         onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
       >
         <div className="w-5 h-9 border-2 border-current rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-current rounded-full" />
         </div>
       </motion.div>
    </section>
  );
}
